/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { type ProductModel } from '@/api/types/products-types';
import SuperSkuStyles from './SuperSkuStyles.module.scss';
import cx from 'classnames';
import { View, Text, Actionable } from '@az/starc-ui';
import { useRouter } from 'next/navigation';
import Image from '@/components/NextImage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { WiperBladeNotification } from '../WiperBladeNotification/WiperBladeNotification';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { SuperSkusImagesSelector } from '../SuperSkusImages/SuperSkusImagesSelector';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getVisibleItemCount } from '@/utils/getVisibleItemCount';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

// small threshold to account for decimal rounding
const SCROLL_THRESHOLD = 2;
const WIPER_BLADES_PROODUCT_ID = 'prod1720001';

type Props = {
  optionName: string;
  availableOptions?: ProductModel['availableOptions'];
  choices?: ProductModel['choices'];
  currentSelection?: Record<string, string>;
  shouldShowSkeleton?: boolean;
};

const SuperSkuOptionSelector = ({
  optionName,
  availableOptions,
  choices,
  currentSelection,
  shouldShowSkeleton,
}: Props) => {
  const router = useRouter();
  const optionsRef = useRef<HTMLDivElement>(null);
  const [shouldHideRightArrow, setShouldHideRightArrow] = useState(false);
  const [shouldHideLeftArrow, setShouldHideLeftArrow] = useState(true);
  const { setPageSpecificDataAsync } = usePageSpecificData();
  const options = availableOptions?.[optionName];
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const [scrollLeftSignal, setScrollLeftSignal] = useState(0);
  const lastAutoLoadedScrollWidthRef = useRef(0);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const checkScrollPosition = useCallback(() => {
    if (optionsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = optionsRef.current;
      // Hide left arrow when scrolled all the way to the left
      setShouldHideLeftArrow(scrollLeft <= 0);

      // Hide right arrow when scrolled all the way to the right
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_THRESHOLD;
      setShouldHideRightArrow(isAtEnd);
      // Auto-load next page when scrolled near the right edge (80% threshold)
      const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;
      if (scrollPercentage >= 0.8 && !isAtEnd) {
        if (lastAutoLoadedScrollWidthRef.current !== scrollWidth) {
          lastAutoLoadedScrollWidthRef.current = scrollWidth;
          setScrollLeftSignal((n) => n + 1);
        }
      }
    }
  }, []);

  const scroll = (direction: 1 | -1) => {
    const container = optionsRef.current;
    if (!container) {
      return;
    }
    const items = Array.from(container.children) as HTMLElement[];
    if (!items.length) {
      return;
    }

    let currentIndex = 0;
    items.forEach((item, index) => {
      if (item.offsetLeft <= container.scrollLeft + SCROLL_THRESHOLD) {
        currentIndex = index;
      }
    });

    const origin = items[currentIndex].offsetLeft;
    const itemExtents = items
      .slice(currentIndex)
      .map((item) => item.offsetLeft - origin + (item.offsetWidth || container.clientWidth));
    const visibleItemCount = getVisibleItemCount(container.clientWidth, itemExtents);
    const step = Math.max(visibleItemCount - 1, 1);
    const targetIndex = Math.min(Math.max(currentIndex + step * direction, 0), items.length - 1);

    container.scrollTo({ left: items[targetIndex].offsetLeft, behavior: 'smooth' });
    if (direction > 0) {
      setScrollLeftSignal((n) => n + 1);
    }
  };

  // Determines which options are disabled given the current selection
  const enabledOptionValues = useMemo<string[]>(() => {
    if (Object.getOwnPropertyNames(currentSelection).length < 2) {
      return choices?.map((choice) => choice.choice[optionName]) || [];
    }

    const otherOptionNames = Object.getOwnPropertyNames(currentSelection).filter(
      (key) => key !== optionName,
    );

    const enabledChoices = choices?.filter((choice) =>
      otherOptionNames.every(
        (otherOptionName) => choice.choice[otherOptionName] === currentSelection?.[otherOptionName],
      ),
    );

    return enabledChoices?.map((choice) => choice.choice[optionName]) || [];
  }, [choices, currentSelection, optionName]);

  const handleOptionClick = async (option: { selected: boolean; value: string }) => {
    if (option.selected) {
      return;
    }
    const newSelection = {
      ...currentSelection,
      [optionName]: option.value,
    };
    const newChoice = choices?.find(({ choice }) =>
      Object.entries(newSelection).every(([key, value]) => choice[key] === value),
    );

    if (newChoice) {
      const productName = productDetails?.product?.itemDescription ?? '';
      const navigationLink = `${productName} | ${option.value}`;
      await setPageSpecificDataAsync({
        productFindingMethod: 'Finder',
        pfmLocation: 'SuperSku',
        finderFeature: 'superSku',
        navigationInteraction: 'navigationInteraction',
        navigationLink,
      });

      const newUrl =
        enableSuperSkuImages && isMobile
          ? newChoice.choice.productDetailsPageUrl + '#productCarouselContainer'
          : newChoice.choice.productDetailsPageUrl;

      router.push(newUrl, {
        scroll: false,
      });
    }
  };
  const { data: productDetails } = useProductDetails({ enabled: false });
  const preferredVehicle = usePreferredVehicle();

  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition, options]);

  useEffect(() => {
    const container = optionsRef.current;
    container?.addEventListener('scroll', checkScrollPosition);
    container?.addEventListener('touch', checkScrollPosition);

    return () => {
      container?.removeEventListener('scroll', checkScrollPosition);
      container?.removeEventListener('touch', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  const hasAtLeastOneImage = (options ?? []).some((option) => Boolean(option?.imageUrl));
  const enableSuperSkuImages = hasAtLeastOneImage && (options?.length ?? 0) >= 2;

  return (
    <View
      direction="column"
      className={cx(SuperSkuStyles.superSkuOptionSelector, {
        [SuperSkuStyles.superSkuImageOptionsGap]:
          enableSuperSkuImages && isProductDetailsPageV2Enabled,
        [SuperSkuStyles.optionsGap]: isProductDetailsPageV2Enabled && !enableSuperSkuImages,
        [SuperSkuStyles.optionsGapV2]: isProductDetailsPageV2Enabled && enableSuperSkuImages,
      })}
    >
      <View direction="row" attributes={{ 'data-testid': 'super-sku-selected-option' }}>
        <Text
          weight="bold"
          className={cx(
            SuperSkuStyles.optionName,
            shouldShowSkeleton && SuperSkuStyles.loadingInformation,
          )}
        >
          {optionName}:
        </Text>
        <Text
          className={cx(
            SuperSkuStyles.selectedOption,
            shouldShowSkeleton && SuperSkuStyles.loadingInformation,
          )}
        >
          {currentSelection?.[optionName]}
        </Text>
      </View>
      {preferredVehicle?.catalogVehicleId !== undefined &&
      productDetails?.product?.ecommProductId === WIPER_BLADES_PROODUCT_ID ? (
        <View>
          <WiperBladeNotification />
        </View>
      ) : null}
      <View.Item
        gapBefore={0}
        className={cx({
          [SuperSkuStyles.superSkuImagesMarginPdpV2Enabled]:
            enableSuperSkuImages && isProductDetailsPageV2Enabled,
        })}
      >
        <View direction="row" align="center" className={SuperSkuStyles.optionsContainer}>
          <View
            direction="row"
            align="center"
            wrap={false}
            className={cx(enableSuperSkuImages ? SuperSkuStyles.images : SuperSkuStyles.options)}
            attributes={{ ref: optionsRef, 'data-testid': 'options-container' }}
          >
            {enableSuperSkuImages ? (
              <SuperSkusImagesSelector
                options={options}
                handleClick={handleOptionClick}
                choices={choices}
                optionName={optionName}
                scrollLeftSignal={scrollLeftSignal}
                enabledOptions={enabledOptionValues}
                availableOptions={availableOptions}
                shouldShowSkeleton={shouldShowSkeleton}
              ></SuperSkusImagesSelector>
            ) : (
              options?.map((option) => (
                <Actionable
                  key={option.value}
                  disabled={!enabledOptionValues.includes(option.value) || shouldShowSkeleton}
                  onClick={() => handleOptionClick(option)}
                  className={cx(
                    SuperSkuStyles.optionButton,
                    {
                      [SuperSkuStyles.selectedOptionButton]: option.selected,
                      [SuperSkuStyles.disabledOptionButton]: !enabledOptionValues.includes(
                        option.value,
                      ),
                      [SuperSkuStyles.disabledOptionsV2]: !enabledOptionValues.includes(
                        option.value,
                      ),
                    },
                    shouldShowSkeleton && SuperSkuStyles.loadingInformationContainer,
                  )}
                >
                  {option.value}
                </Actionable>
              ))
            )}
          </View>
          <Actionable
            attributes={{ 'data-testid': 'scroll-left-button' }}
            onClick={() => scroll(-1)}
            className={cx(SuperSkuStyles.arrowButton, SuperSkuStyles.arrowButtonLeft, {
              [SuperSkuStyles.hidden]: shouldHideLeftArrow,
              [SuperSkuStyles.topMargin]: enableSuperSkuImages,
            })}
          >
            <Image
              src="/images/left-arrow-grey.svg"
              alt="scroll carousel left"
              height={16}
              width={enableSuperSkuImages ? 16 : 8}
            />
          </Actionable>
          <Actionable
            attributes={{ 'data-testid': 'scroll-right-button' }}
            onClick={() => scroll(1)}
            className={cx(SuperSkuStyles.arrowButton, SuperSkuStyles.arrowButtonRight, {
              [SuperSkuStyles.hidden]: shouldHideRightArrow,
              [SuperSkuStyles.topMargin]: enableSuperSkuImages,
            })}
          >
            <Image
              src="/images/left-arrow-grey.svg"
              alt="scroll carousel right"
              height={16}
              width={enableSuperSkuImages ? 16 : 8}
            />
          </Actionable>
        </View>
      </View.Item>
    </View>
  );
};

export default SuperSkuOptionSelector;
